import React, { useState, useMemo } from 'react';
import { Category, CategoryWithChildren } from './types';
import { useDirtyChangeReport } from '../../../hooks/useDirtyChangeReport';
import '../../../components/common/CRMForm.css';
import './CategoryForm.css';

interface CategoryFormProps {
  selectedParentCategory: CategoryWithChildren | null;
  onCreateCategory: (data: { name: string; parentId: number }) => Promise<void>;
  onUpdateCategory: (id: number, data: { name?: string; isActive?: boolean; tax?: boolean }) => Promise<void>;
  onDeleteCategory: (id: number) => Promise<void>;
  loading?: boolean;
  onSaveSuccess?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function CategoryForm({ 
  selectedParentCategory, 
  onCreateCategory, 
  onUpdateCategory, 
  onDeleteCategory,
  loading = false,
  onSaveSuccess,
  onDirtyChange
}: CategoryFormProps) {
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isTaxCategory, setIsTaxCategory] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isExpenseParentSelection =
    !!selectedParentCategory && selectedParentCategory.name.toLowerCase() === 'expense';

  const hasChanges = useMemo(() => {
    if (editingCategory) {
      const taxChanged = isExpenseParentSelection && isTaxCategory !== (editingCategory.tax === 1);
      return name.trim() !== editingCategory.name
        || isActive !== !!editingCategory.isActive
        || taxChanged;
    }
    if (showCreateForm) {
      return name.trim() !== ''
        || !isActive
        || (isExpenseParentSelection && isTaxCategory);
    }
    return false;
  }, [
    editingCategory,
    showCreateForm,
    name,
    isActive,
    isTaxCategory,
    isExpenseParentSelection
  ]);

  useDirtyChangeReport(hasChanges, onDirtyChange);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Category name is required');
      return;
    }

    if (!selectedParentCategory) {
      setError('No parent category selected');
      return;
    }

    setSaving(true);
    try {
      await onCreateCategory({ name: name.trim(), parentId: selectedParentCategory.id });
      setName('');
      setShowCreateForm(false);
      onSaveSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setName(category.name);
    setIsActive(!!category.isActive);
    setIsTaxCategory(category.tax === 1);
    setShowCreateForm(false);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Category name is required');
      return;
    }

    if (!editingCategory) return;

    setSaving(true);
    try {
      await onUpdateCategory(editingCategory.id, {
        name: name.trim(),
        isActive,
        ...(isExpenseParentSelection ? { tax: isTaxCategory } : {})
      });
      setEditingCategory(null);
      setName('');
      setIsActive(true);
      setIsTaxCategory(false);
      onSaveSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category: Category) => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"?`)) {
      return;
    }
    
    try {
      await onDeleteCategory(category.id);
      onSaveSuccess?.();
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Failed to delete category: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleCancel = () => {
    setEditingCategory(null);
    setShowCreateForm(false);
    setName('');
    setIsActive(true);
    setIsTaxCategory(false);
    setError(null);
  };

  const isDeleteDisabled = (category: Category) => {
    // Use the isDeletable field from the API response
    // This is calculated server-side based on usage and child categories
    return !category.isDeletable;
  };

  const getDeleteButtonTitle = (category: Category) => {
    // Use the isDeletable field from the API response for tooltip
    if (!category.isDeletable) {
      return 'Cannot delete: category has associations or child categories';
    }
    return 'Delete category';
  };

  if (!selectedParentCategory) {
    return (
      <div className="form">
        <div className="app-welcome-message">
          <h2>Category Management</h2>
          <p>Select a category type from the list to view and manage its categories.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form">
      <div className="form-header">
        <h2>{selectedParentCategory.name} Categories</h2>
        <button 
          className="app-btn app-btn-primary"
          onClick={() => {
            setShowCreateForm(true);
            setEditingCategory(null);
            setName('');
            setIsActive(true);
            setIsTaxCategory(false);
            setError(null);
          }}
          disabled={saving || loading}
        >
          Add {selectedParentCategory.name} Category
        </button>
      </div>

      {(showCreateForm || editingCategory) && (
        <div className="form-section">
          <h3>{editingCategory ? 'Edit Category' : 'Create Category'}</h3>
          <div className="form-section-content">
            <form onSubmit={editingCategory ? handleSaveEdit : handleCreate}>
              <div className="app-form-group">
                <label htmlFor="categoryName" className="app-form-label required">
                  Category Name
                </label>
                <input
                  type="text"
                  id="categoryName"
                  className="app-form-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter category name"
                  disabled={saving || loading}
                  required
                />
              </div>

              {editingCategory && (
                <div className="app-form-group">
                  <div className="category-form-checkbox-row">
                    <label className="app-checkbox-label">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        disabled={saving || loading}
                      />
                      Active
                    </label>
                    {isExpenseParentSelection && (
                      <label className="app-checkbox-label">
                        <input
                          type="checkbox"
                          checked={isTaxCategory}
                          onChange={(e) => setIsTaxCategory(e.target.checked)}
                          disabled={saving || loading}
                        />
                        Tax
                      </label>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="app-error-message">{error}</div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="app-btn app-btn-secondary"
                  onClick={handleCancel}
                  disabled={saving || loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="app-btn app-btn-primary"
                  disabled={saving || loading || !name.trim()}
                >
                  {saving ? 'Saving...' : editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="form-section">
        <h3>Categories ({selectedParentCategory.children?.length || 0})</h3>
        <div className="form-section-content">
          {selectedParentCategory.children && selectedParentCategory.children.length > 0 ? (
            selectedParentCategory.children.map((child) => (
              <div key={child.id} className="form-item-compact-group">
                <div className="category-section-item-info">
                  <div className="category-section-item-name">{child.name}</div>
                  <div className="category-section-item-status-row">
                    <div className={`category-section-item-status ${child.isActive ? 'active' : 'inactive'}`}>
                      {child.isActive ? 'Active' : 'Inactive'}
                    </div>
                    {child.tax === 1 && (
                      <div className="category-section-item-status active">Tax</div>
                    )}
                  </div>
                </div>
                <div className="form-item-compact-actions">
                  <button 
                    className="app-btn app-btn-secondary app-btn-small"
                    onClick={() => handleEdit(child)}
                    disabled={saving || loading}
                  >
                    Edit
                  </button>
                  <button 
                    className="app-btn app-btn-danger app-btn-small"
                    onClick={() => handleDelete(child)}
                    disabled={isDeleteDisabled(child) || saving || loading}
                    title={getDeleteButtonTitle(child)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="list-no-items">
              No {selectedParentCategory.name.toLowerCase()} categories yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
